import { Box, Container, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import lodash from 'lodash';
import theme from '../../../../theme';
import { getRGBAFromColor } from '../../../../utils/methods/getCssVariables';
import FieldErrorText from '../../../../components/ui/FieldErrorText';
import NavigationButtons from '../NavigationButtons';
import { useDispatch, useSelector } from 'react-redux';
import {
  rootContainer,
  contentContainer,
  formContainer,
  formStyle,
  questionsStack,
  introContainer,
  introTextMain,
  introTextSecondary,
  questionContainer,
  questionRow,
  questionText,
  toggleButtonGroup,
  getToggleButtonStyles,
  errorContainer,
  explanationField,
} from './QuestionareSx';
import {
  setActiveStep,
  setMyGarageMultipleFields,
  setQuestionareForm,
} from '../../../../toolkit/myGarage/myGarageSlice';
import { CarSellQuestionnaire, MyGarageSteps } from '../../../../utils/constants/myGarage';
import { useLazyUpdateCarQuery, useUpdateCarOnRelistPendingMutation } from '../../../../toolkit/myGarage/myGarageApi';
import { setAlertDialog } from '../../../../toolkit/alertDialog/alertDialogSlice';
import { emptyFieldsPartialForm } from './utils/emptyFieldsPartialForm';
import { backendMappedValuesStep1 } from './GeneralInfo';
import { useTranslation } from 'react-i18next';
import { isVehicleDataEditEnabled } from '../../../../utils/methods/generalVehicleMethods';
import { useGetVehicleDetailsQuery, useLazyGetVehicleDetailsQuery } from '../../../../toolkit/carDetails/carDetailsApi';
import { CAR_STATUS } from '../../../../utils/constants/carData';

const Questionnaire = ({ previousFormHandler }) => {
  const { t } = useTranslation('questionareForm');

  const onMount = React.useRef({
    setQuestionareForm: false,
  });

  const dispatch = useDispatch();
  const { forms, previouslySubmittedForms, vehicle_id, selectedVehicle } = useSelector((store) => store.myGarage);
  const auth = useSelector((store) => store.auth);

  const generalInfoForm = forms?.generalInfo;
  const questionareForm = forms?.questionare;
  const specificationsForm = forms?.specifications;
  const galleryForm = forms?.gallery;

  const [updateCarApiHandler, updateCarApiData] = useLazyUpdateCarQuery();
  const [updateCarOnRelistPendingApiHandler, updateCarOnRelistPendingApiData] = useUpdateCarOnRelistPendingMutation();
  const [getVehicleDetailsApiHandler, getVehicleDetailsApiData] = useLazyGetVehicleDetailsQuery();

  const validationSchema = Yup.object().shape({
    ...CarSellQuestionnaire.reduce((schema, question, index) => {
      console.log(index, `CarSellQuestionnaire`);
      if (index === 3 || index === 5) {
        schema[question.name] = Yup.mixed().required(t('validationFieldRequired')).oneOf([0, 1]);
      } else {
        schema[question.name] = Yup.mixed().nullable(true).oneOf([0, 1, null]);
      }
      return schema;
    }, {}),
    vehicle_drivable_not_explanation: Yup.string().when('Vehicle_Drivable', {
      is: 0,
      then: () => Yup.string().max(500).required(t('validationFieldRequired')),
    }),
  });

  const initialValues = CarSellQuestionnaire.reduce(
    (values, question) => {
      values[question.name] = '';
      return values;
    },
    { vehicle_drivable_not_explanation: '' }
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const payload = {};
      const formData = new FormData();

      // for (let i = 0; i < Object.entries(emptyFieldsPartialForm)?.length; i++) {
      //   payload[
      //     [Object.entries(emptyFieldsPartialForm)?.[i]?.[0]]
      //   ] = Object.entries(emptyFieldsPartialForm)?.[i]?.[1];
      // }

      for (let i = 0; i < Object.entries(values)?.length; i++) {
        payload[Object.entries(values)?.[i]?.[0]] = !Object.entries(values)?.[i]?.[1]
          ? 0
          : Object.entries(values)?.[i]?.[1];
      }

      for (let i = 0; i < Object.entries(payload)?.length; i++) {
        // payload[Object.entries(payload)?.[i]?.[0]] =
        //   Object.entries(payload)?.[i]?.[1];
        formData.append(Object.entries(payload)?.[i]?.[0], Object.entries(payload)?.[i]?.[1]);
      }

      formData.append(`Seller_Id`, auth?.user_id);

      if (!lodash?.isEqual(previouslySubmittedForms?.questionare, formik?.values)) {
        let updateCarResponse;

        if (getVehicleDetailsApiData?.data?.vehicle?.car_label_status === CAR_STATUS.RELIST_PENDING) {
          updateCarResponse = await updateCarOnRelistPendingApiHandler({
            formData,
            Vehicle_Id: vehicle_id,
          });
        } else {
          updateCarResponse = await updateCarApiHandler({
            formData,
            endpoint: `/updatevehicle/${vehicle_id}/normaledit`,
          });
        }

        if (
          updateCarResponse?.isSuccess ||
          ('status' in (updateCarResponse?.data || {}) && updateCarResponse?.data?.status)
        ) {
          dispatch(
            setMyGarageMultipleFields({
              previouslySubmittedForms: {
                ...previouslySubmittedForms,
                questionare: formik?.values,
              },
              forms: {
                ...previouslySubmittedForms,
                questionare: formik?.values,
              },
            })
          );
          // dispatch(setQuestionareForm(values));
          dispatch(setActiveStep(MyGarageSteps[2]));
        } else {
          dispatch(
            setAlertDialog({
              open: true,
              title: t('errorTitle'),
              message: updateCarResponse?.error?.message || t('someErrorOccurred'),
            })
          );
        }
      } else {
        dispatch(setActiveStep(MyGarageSteps[2]));
      }
    },
  });

  React.useEffect(() => {
    if (forms?.questionare) {
      formik.setValues(forms?.questionare);
    }
  }, []);

  React.useEffect(() => {
    if (onMount.current.setQuestionareForm) {
      dispatch(setQuestionareForm(formik?.values));
    } else {
      onMount.current.setQuestionareForm = true;
    }
  }, [formik?.values]);

  React.useEffect(() => {
    if (!vehicle_id) {
      return;
    }

    (async () => {
      await getVehicleDetailsApiHandler({
        vehicleId: vehicle_id,
      });
    })();
  }, [vehicle_id]);

  return (
    <Box sx={rootContainer}>
      <Box sx={contentContainer}>
        <Container sx={formContainer}>
          <form style={formStyle} onSubmit={formik.handleSubmit}>
            <Stack sx={questionsStack}>
              <Box sx={introContainer}>
                <Typography variant="subtitle1" sx={introTextMain}>
                  {t('questionnaireIntroTextMain')}
                </Typography>
                <Typography variant="subtitle2" sx={introTextSecondary}>
                  {t('questionnaireIntroTextSecondary')}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {CarSellQuestionnaire.map(({ name, label, required }) => (
                  <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12 }} key={name}>
                    <Box sx={questionContainer}>
                      <Box sx={questionRow}>
                        <Typography variant="body1" sx={questionText}>
                          {t(`questionnaire${name}Label`)} {required ? <span style={{ color: 'red' }}>*</span> : null}
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          value={formik.values[name]}
                          onChange={(_, value) => {
                            console.log(value, `setFieldValue`);
                            formik.setFieldValue(name, value);
                          }}
                          sx={toggleButtonGroup}
                          disabled={
                            !isVehicleDataEditEnabled({
                              car: selectedVehicle,
                            })
                          }
                        >
                          <ToggleButton value={1} sx={getToggleButtonStyles(1, formik.values[name])}>
                            {t('yes')}
                          </ToggleButton>
                          <ToggleButton value={0} sx={getToggleButtonStyles(0, formik.values[name])}>
                            {t('no')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {formik?.touched?.[name] && formik?.errors?.[name] && (
                        <Box sx={errorContainer}>
                          <FieldErrorText>{formik?.errors?.[name]}</FieldErrorText>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {formik.values.Vehicle_Drivable === 0 && (
                <TextField
                  fullWidth
                  label={t('pleaseExplainWhy')}
                  name="vehicle_drivable_not_explanation"
                  value={formik.values.vehicle_drivable_not_explanation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.vehicle_drivable_not_explanation &&
                    Boolean(formik.errors.vehicle_drivable_not_explanation)
                  }
                  helperText={
                    formik.touched.vehicle_drivable_not_explanation && formik.errors.vehicle_drivable_not_explanation
                  }
                  sx={explanationField}
                  disabled={
                    !isVehicleDataEditEnabled({
                      car: selectedVehicle,
                    })
                  }
                />
              )}
            </Stack>

            <NavigationButtons
              onSubmit={formik.handleSubmit}
              isLoading={updateCarApiData?.isFetching || updateCarOnRelistPendingApiData?.isLoading}
              previousFormHandler={previousFormHandler}
            />
          </form>
        </Container>
      </Box>
    </Box>
  );
};

export default Questionnaire;
